import { MailDetailLayout } from "../components/MailDetailLayout";
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { emailsApi, ReclassifyRequest } from "../api/emails";
import { SkeletonText } from "../components/Skeleton";
import AuditTrailModal from "../components/AuditTrailModal";
import {
  Paperclip,
  Image as ImageIcon,
  FileText,
  Tag,
  Mail,
  Bot,
  Clock,
  User,
  GitBranch,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";

export function EmailDetailPage({
  emailId: providedEmailId,
  embedded = false,
}: { emailId?: string; embedded?: boolean } = {}) {
  const { emailId: routeEmailId } = useParams<{ emailId: string }>();
  const emailId = providedEmailId || routeEmailId;

  const [showRaw, setShowRaw] = useState(false);
  const [showReclassify, setShowReclassify] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [reclassifyForm, setReclassifyForm] = useState<ReclassifyRequest>({});

  const queryClient = useQueryClient();

  const { data: email, isLoading } = useQuery({
    queryKey: ["email-detail", emailId],
    queryFn: () => emailsApi.get(emailId!),
    enabled: !!emailId,
  });

  const reclassifyMutation = useMutation({
    mutationFn: (data: ReclassifyRequest) =>
      emailsApi.reclassify(emailId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-detail", emailId] });
      queryClient.invalidateQueries({ queryKey: ["emails-list"] });
      setShowReclassify(false);
      setReclassifyForm({});
    },
    onError: () => {
      alert("Errore durante la riclassifica");
    },
  });

  if (isLoading) {
    return (
      <MailDetailLayout
        embedded={embedded}
        breadcrumbs={[
          { label: "Email Archive", href: "/emails" },
          { label: "Caricamento..." },
        ]}
      >
        <SkeletonText lines={10} />
      </MailDetailLayout>
    );
  }

  if (!email) {
    return (
      <MailDetailLayout
        embedded={embedded}
        breadcrumbs={[
          { label: "Email Archive", href: "/emails" },
          { label: "Non trovata" },
        ]}
      >
        <div className="flex-1 p-6 text-center text-muted-foreground">
          Email non trovata
        </div>
      </MailDetailLayout>
    );
  }

  const isInbound = email.direction === "INBOUND";

  return (
    <MailDetailLayout
      embedded={embedded}
      breadcrumbs={[
        { label: "Email Archive", href: "/emails" },
        { label: email.subject || "Email" },
      ]}
      topbarActions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRaw(!showRaw)}
          >
            {showRaw ? (
              <FileText className="size-3.5" />
            ) : (
              <Mail className="size-3.5" />
            )}
            {showRaw ? "HTML" : "Raw"}
          </Button>
          {email.case_id && (
            <Link to={`/cases/${email.case_id}`}>
              <Button size="sm">Vai al caso</Button>
            </Link>
          )}
        </>
      }
      mainClassName="mail-email-content"
    >
      <header className="mail-message-heading">
        <h2>{email.subject || "Senza oggetto"}</h2>
      </header>
      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Badge
          variant="outline"
          className={
            (email.priority === "urgent" || email.priority === "high")
              ? "bg-urgent/10 text-urgent border-urgent/20"
              : email.priority === "medium"
                ? "bg-medium/10 text-medium border-medium/20"
                : email.priority === "low"
                  ? "bg-success/10 text-success border-success/20"
                  : "bg-muted text-muted-foreground border-border"
          }
        >
          {email.priority || "no priority"}
        </Badge>
        <Badge
          variant="outline"
          className="bg-info/10 text-info border-info/20"
        >
          {email.category || "no category"}
        </Badge>
        <Badge
          variant="outline"
          className={
            email.status === "new"
              ? "bg-muted text-muted-foreground border-border"
              : "bg-success/10 text-success border-success/20"
          }
        >
          {email.status}
        </Badge>
        <Badge
          variant="outline"
          className={
            isInbound
              ? "bg-info/10 text-info border-info/20"
              : "bg-success/10 text-success border-success/20"
          }
        >
          {isInbound ? "Ricevuta" : "Inviata"}
        </Badge>
        {email.needs_patient_confirmation && (
          <Badge
            variant="outline"
            className="bg-warning/10 text-warning border-warning/20"
          >
            Richiede conferma
          </Badge>
        )}
      </div>

      {/* Meta */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground flex items-center gap-1.5">
                <User className="size-3.5" />
                Da:
              </span>
              <span className="font-medium text-foreground ml-1">
                {email.from_email}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground flex items-center gap-1.5">
                <Clock className="size-3.5" />
                Data:
              </span>
              <span className="font-medium text-foreground ml-1">
                {email.received_at
                  ? new Date(email.received_at).toLocaleString("it-IT")
                  : "-"}
              </span>
            </div>
            {email.patient_name && (
              <div>
                <span className="text-muted-foreground">Paziente:</span>{" "}
                <span className="font-medium text-foreground">
                  {email.patient_name}
                </span>
              </div>
            )}
            {email.owner_name && (
              <div>
                <span className="text-muted-foreground">Proprietario:</span>{" "}
                <span className="font-medium text-foreground">
                  {email.owner_name}
                </span>
              </div>
            )}
          </div>

          {email.summary_ai && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-1.5 mb-2">
                <Bot className="size-4 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  Riassunto AI
                </span>
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed">
                {email.summary_ai}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reclassify */}
      {showReclassify && (
        <Card className="mb-4">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              Riclassifica Email
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Categoria</Label>
                <Select
                  value={reclassifyForm.category || email.category || undefined}
                  onValueChange={(v) =>
                    setReclassifyForm({ ...reclassifyForm, category: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visita">Visita</SelectItem>
                    <SelectItem value="referto">Referto</SelectItem>
                    <SelectItem value="richiesta_info">
                      Richiesta info
                    </SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorità</Label>
                <Select
                  value={reclassifyForm.priority || email.priority || undefined}
                  onValueChange={(v) =>
                    setReclassifyForm({ ...reclassifyForm, priority: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="medium">Media</SelectItem>
                    <SelectItem value="low">Bassa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => reclassifyMutation.mutate(reclassifyForm)}
                disabled={reclassifyMutation.isPending}
                size="sm"
              >
                {reclassifyMutation.isPending ? "Salvataggio..." : "Applica"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowReclassify(false);
                  setReclassifyForm({});
                }}
              >
                Annulla
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowReclassify(!showReclassify)}
        >
          <Tag className="size-3.5" />
          Riclassifica
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAuditTrail(true)}
        >
          <GitBranch className="size-3.5" />
          Audit Trail
        </Button>
      </div>

      {/* Body */}
      {showRaw ? (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              Raw Body (debug)
            </h3>
            <pre className="text-xs bg-muted p-4 rounded overflow-x-auto max-h-[600px] whitespace-pre-wrap font-mono">
              {email.body_raw || "Nessun contenuto"}
            </pre>
          </CardContent>
        </Card>
      ) : email.body_html ? (
        <Card>
          <CardContent className="p-4">
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: email.body_html }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-4">
            <pre className="text-sm whitespace-pre-wrap font-sans break-words text-foreground/80">
              {email.body_raw || "Nessun contenuto"}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Attachments */}
      {email.attachments && email.attachments.length > 0 && (
        <Card className="mt-4">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
              <Paperclip className="size-4 text-muted-foreground" />
              Allegati ({email.attachments.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {email.attachments.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm hover:bg-muted/50 transition-colors"
                >
                  {att.is_image ? (
                    <ImageIcon className="size-4 text-muted-foreground" />
                  ) : att.is_dicom ? (
                    <FileText className="size-4 text-muted-foreground" />
                  ) : (
                    <Paperclip className="size-4 text-muted-foreground" />
                  )}
                  <span className="break-all text-foreground/80">
                    {att.filename}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    ({(att.size_bytes / 1024).toFixed(0)} KB)
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit Trail Modal */}
      {emailId && (
        <AuditTrailModal
          emailId={emailId}
          open={showAuditTrail}
          onOpenChange={setShowAuditTrail}
        />
      )}
    </MailDetailLayout>
  );
}
